// src/pages/LandingPage.jsx
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { styles } from '../styles/newstyles';
import '../styles/landing.css';
import '../styles/dashboard.css';
import { FiArrowRight, FiFileText, FiCheckSquare, FiUsers, FiBarChart2, FiSettings, FiUpload, FiPlus, FiClipboard } from 'react-icons/fi';
import { getCurrentProfile, subscribeCurrentProfile } from '../services/currentProfile';
import { getTickets } from '../services/tickets';
import { ticketLabel, formatTicketSummary } from '../utils/ticketSummary';
import { formatAssigneeName } from '../utils/ticketAvatar';

const recentFirst = (a, b) => (Date.parse(b.UpdatedAt) || 0) - (Date.parse(a.UpdatedAt) || 0) || Number(b.TicketID) - Number(a.TicketID);
const previewTickets = (tickets, completed = false) => [...tickets].sort((a, b) => {
  const critical = (ticket) => String(ticket.PriorityCode || ticket.PriorityName || '').toUpperCase() === 'CRITICAL';
  return (completed ? 0 : Number(critical(b)) - Number(critical(a))) || recentFirst(a, b);
}).slice(0, 2);

const LandingPage = ({ onLogout, isAdmin, username, canEditRestricted, canEditDomains, canEditCategories }) => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [installLabel, setInstallLabel] = useState('Install app');
  const profile = useSyncExternalStore(subscribeCurrentProfile, getCurrentProfile, getCurrentProfile);
  const [ticketCounts, setTicketCounts] = useState(null);
  const [countsError, setCountsError] = useState(false);
  const [ticketPreviews, setTicketPreviews] = useState({});
  useEffect(() => {
    if (!profile?.userID) return undefined;
    let live = true;
    setTicketCounts(null); setTicketPreviews({}); setCountsError(false);
    const filters = { mine: true, ...(isAdmin ? { assigneeId: String(profile.userID) } : {}) };
    Promise.all([getTickets(filters), getTickets({ ...filters, completed: true })]).then(([active, completed]) => {
      if (!live) return;
      const rows = active.data?.tickets || [];
      setTicketPreviews({ todo: previewTickets(rows.filter((item) => (item.StatusCategory || item.StatusCode) === 'TODO')), progress: previewTickets(rows.filter((item) => (item.StatusCategory || item.StatusCode) === 'IN_PROGRESS')), completed: previewTickets(completed.data?.tickets || [], true) });
      setTicketCounts({ todo: rows.filter((item) => (item.StatusCategory || item.StatusCode) === 'TODO').length, progress: rows.filter((item) => (item.StatusCategory || item.StatusCode) === 'IN_PROGRESS').length, completed: (completed.data?.tickets || []).length });
    }).catch(() => { if (live) setCountsError(true); });
    return () => { live = false; };
  }, [profile?.userID, isAdmin]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Hide CTA entirely if we're already running as an installed app
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone) {
      setShowInstall(false);
      return undefined;
    }

    // if global prompt was already captured (e.g., first page load), show CTA immediately
    if (typeof window !== 'undefined' && window.__typortalInstallPrompt) {
      setDeferredPrompt(window.__typortalInstallPrompt);
      setShowInstall(true);
    }

    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent || '';
      const isMobile =
        /android|iphone|ipad|ipod/i.test(ua) ||
        (typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(pointer: coarse)').matches);
      setInstallLabel(isMobile ? 'Add to Home screen' : 'Install on this device');
    }

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
      window.__typortalInstallPrompt = e;
    };

    const onAppInstalled = () => {
      setShowInstall(false);
      setDeferredPrompt(null);
      if (typeof window !== 'undefined') {
        window.__typortalInstallPrompt = null;
      }
    };

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstall(false);
    window.__typortalInstallPrompt = null;
  };

  const workspaces = [
    { name: isAdmin ? 'Tickets' : 'My requests', description: isAdmin ? 'View and manage tickets.' : 'View your requests.', path: '/tickets', Icon: FiCheckSquare },
    { name: 'Documents', description: 'Search and view uploaded documents.', path: '/home', Icon: FiFileText },
    { name: 'Owners', description: 'Find owner and property details.', path: '/owner-details', Icon: FiUsers },
    { name: 'Protests', description: 'View protests and hearing details.', path: '/protests', Icon: FiClipboard },
    { name: 'Contracts', description: 'View contract documents.', path: '/contracts', Icon: FiFileText },
  ];
  return <div className="landing-page" style={styles.mainContainer}>
    <Navbar username={username} onLogout={onLogout} isAdmin={isAdmin} />
    <main className="dashboard-content dashboard-home" style={{ '--dashboard-primary': styles.heading.color, '--dashboard-header': styles.th.background, '--dashboard-text': styles.td.color, '--dashboard-font': styles.mainContainer.fontFamily, '--dashboard-text-size': styles.td.fontSize }}>
      <header className="dashboard-header dashboard-welcome">
        <div className="dashboard-welcome-copy"><span className="dashboard-welcome-eyebrow">THOMAS Y. PICKETT</span><h1>Welcome back{profile?.name?.trim() ? `, ${formatAssigneeName(profile.name)}` : ''}.</h1><p>What would you like to work on today?</p></div>
        <div className="dashboard-actions"><button className="dashboard-action dashboard-action--primary dashboard-action--upload" type="button" onClick={() => navigate('/add')}><FiUpload /> Upload document</button><button className="dashboard-action" type="button" onClick={() => navigate('/tickets/new')}><FiPlus /> Create request</button></div>
      </header>
      <div className="dashboard-feature-grid">
      <button className="dashboard-card dashboard-chief-card" type="button" onClick={() => navigate('/cao')}>
        <FiBarChart2 className="dashboard-card-icon" aria-hidden="true" /><h2>Chief Dashboard</h2><p>View chief reports and documents.</p><FiArrowRight className="dashboard-card-arrow" aria-hidden="true" />
      </button>
      {isAdmin && <button className="dashboard-card dashboard-administration-card" type="button" onClick={() => navigate('/admin')}><FiSettings className="dashboard-card-icon" aria-hidden="true" /><h2>Administration</h2><p>{[canEditDomains, canEditCategories, canEditRestricted].some(Boolean) ? 'Portal settings and tools' : 'View your administration access'}</p><FiArrowRight className="dashboard-card-arrow" aria-hidden="true" /></button>}
      </div>
      <section aria-label="Ticket queue" className="dashboard-queue">
        <div className="dashboard-section-heading"><h2>Ticket queue</h2></div>
        <div className="dashboard-stat-grid">{[
          { name: 'To do', key: 'todo', value: ticketCounts?.todo, path: '/tickets' },
          { name: 'In progress', key: 'progress', value: ticketCounts?.progress, path: '/tickets' },
          { name: 'Completed', key: 'completed', value: ticketCounts?.completed, path: '/tickets?view=completed' },
        ].map(({ name, key, value, path }) => <div className={`dashboard-queue-card dashboard-queue-card--${key}`} key={key}>
          <button className={`dashboard-stat dashboard-stat--${name.toLowerCase().replace(/\s+/g, '-')}`} type="button" onClick={() => navigate(path)}><span>{name}</span><strong>{value ?? '—'}</strong><FiArrowRight /></button>
          <div className="dashboard-ticket-previews" aria-label={`${name} previews`}>
            {(ticketPreviews[key] || []).filter((ticket) => ticket.TicketID != null).map((ticket) => <button className="dashboard-ticket-preview" key={ticket.TicketID} type="button" onClick={() => navigate(`/tickets/${ticket.TicketID}`)}>
              <span className="dashboard-preview-meta"><span>{ticketLabel(ticket)}</span>{(ticket.PriorityName || ticket.PriorityCode) && <small className={String(ticket.PriorityCode || ticket.PriorityName).toUpperCase() === 'CRITICAL' ? 'dashboard-priority-critical' : ''}>{ticket.PriorityName || ticket.PriorityCode}</small>}</span>
              <span className="dashboard-preview-title">{formatTicketSummary(ticket.Summary)}</span>
            </button>)}
            {value === 0 && <p className="dashboard-preview-empty">No tickets</p>}
          </div>
        </div>)}</div>
        {countsError && <p className="dashboard-muted" role="status">Ticket counts are unavailable. You can still open Tickets.</p>}
      </section>
      <section aria-label="Quick access" className="dashboard-workspaces"><div className="dashboard-section-heading"><h2>Quick access</h2><span className="dashboard-shortcut-caption">Your everyday tools</span></div>
        <div className="dashboard-grid">{workspaces.map(({ name, description, path, Icon }, index) => <button className="dashboard-card dashboard-shortcut" data-workspace={path.slice(1)} type="button" key={path} onClick={() => navigate(path)}><span className="dashboard-shortcut-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span><Icon className="dashboard-card-icon" aria-hidden="true" /><h2>{name}</h2><p>{description}</p><FiArrowRight className="dashboard-card-arrow" aria-hidden="true" /></button>)}</div>
      </section>
      {showInstall && <button type="button" className="dashboard-action dashboard-install" onClick={handleInstall}>{installLabel}</button>}
    </main>
  </div>;
};

export default LandingPage;
